import { inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { patchState, signalStoreFeature, type, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { DateTimeService } from '@shared/datetime/datetime.service';
import { convertFromUTC, SupportedTimezone, toAPIDateTimeStringFormat } from '@shared/datetime/datetime.utils';
import { isEmpty, isEqual, isNil, omitBy } from 'lodash';
import { filter, pairwise, skip } from 'rxjs';

const UTC_TIME_PARAMS = ['fromTime', 'toTime', 'dateTime'] as const;

function toUtcParams(params: Params, dateTimeService: DateTimeService): Params {
  const result: Params = { ...params };
  for (const field of UTC_TIME_PARAMS) {
    if (result[field]) {
      result[field] = toAPIDateTimeStringFormat(result[field], dateTimeService.timezone);
    }
  }
  return result;
}

function fromUtcParams(params: Params, dateTimeService: DateTimeService): Params {
  const result: Params = { ...params };
  for (const field of UTC_TIME_PARAMS) {
    if (result[field]) {
      result[field] = convertFromUTC(result[field], dateTimeService.timezone);
    }
  }
  return result;
}

export function withUrlFilter<T>(routeId: string) {
  return signalStoreFeature(
    {
      state: type<{ pageFilter: T | undefined }>(),
      methods: type<{
        _pageFilterToUrlParams: (pageFilter: T) => Params;
        _urlParamsToPageFilter: (params: Params) => T;
      }>(),
    },
    withProps(() => ({
      _router: inject(Router),
    })),
    withState(() => ({ _pageFilterDisplay: undefined as T | undefined })),

    withMethods(store => {
      const _dateTimeService = inject(DateTimeService);

      const _updatePageFilterFromUrl = (queryParams: Params) => {
        const localParams = fromUtcParams(queryParams, _dateTimeService);
        const pageFilterFromUrl = omitBy(store._urlParamsToPageFilter(localParams) || {}, isNil);
        const currentPageFilter = omitBy(store.pageFilter() || {}, isNil);

        if (
          !isEmpty(queryParams) &&
          isEqual(pageFilterFromUrl, currentPageFilter) &&
          store._router.lastSuccessfulNavigation?.id !== 1
        ) {
          if (store._pageFilterDisplay() !== undefined) {
            patchState(store, { _pageFilterDisplay: undefined });
          }
          return;
        }

        const pageFilter = (isEmpty(queryParams) ? currentPageFilter : pageFilterFromUrl) as T;

        patchState(store, {
          ...setPageFilter(pageFilter),
          _pageFilterDisplay: undefined,
        });
      };

      const _updateUrlParams = (pageFilter: T, queryParams: Params) => {
        const paramsFromPageFilter = omitBy(store._pageFilterToUrlParams(pageFilter), isNil);
        const utcParams = toUtcParams(paramsFromPageFilter, _dateTimeService);
        const currentQueryParams = omitBy(queryParams, isNil);
        if (!isEqual(utcParams, currentQueryParams)) {
          store._router.navigate([], {
            queryParams: utcParams,
          });
        }
      };

      const _setFilterDisplay = (queryParams: Params, previousTimezone: SupportedTimezone) => {
        if (isEmpty(queryParams)) {
          const currentFilter = store.pageFilter() as Record<string, unknown> | undefined;
          if (!currentFilter) {
            return;
          }
          const hasTimeFields = UTC_TIME_PARAMS.some(field => !isNil(currentFilter[field]));
          if (!hasTimeFields) {
            return;
          }

          const converted = { ...currentFilter };
          for (const field of UTC_TIME_PARAMS) {
            const value = currentFilter[field];
            if (!isNil(value)) {
              converted[field] = convertFromUTC(
                toAPIDateTimeStringFormat(value as string, previousTimezone),
                _dateTimeService.timezone,
              );
            }
          }
          patchState(store, { _pageFilterDisplay: converted as T });
          return;
        }
        const localParams = fromUtcParams(queryParams, _dateTimeService);
        const pageFilterFromUrl = omitBy(store._urlParamsToPageFilter(localParams) || {}, isNil) as T;
        const merged = { ...(store.pageFilter() ?? {}), ...pageFilterFromUrl } as T;
        patchState(store, { _pageFilterDisplay: merged });
      };

      const getDisplayPageFilter = () => store._pageFilterDisplay() ?? store.pageFilter();

      return {
        _updatePageFilterFromUrl,
        _updateUrlParams,
        _setFilterDisplay,
        getDisplayPageFilter,
      };
    }),
    withHooks({
      onInit(store, filterChange = toObservable(store.pageFilter)) {
        const dateTimeService = inject(DateTimeService);
        const getQueryParams = () => store._router.routerState.snapshot.root.queryParams;

        filterChange
          .pipe(
            skip(1),
            filter(() => !!getRouteData(store._router)[routeId]),
            takeUntilDestroyed(),
          )
          .subscribe(pageFilter => {
            store._updateUrlParams(pageFilter ?? ({} as T), getQueryParams());
            // Clear display override when user changes the filter
            if (store._pageFilterDisplay() !== undefined) {
              patchState(store, { _pageFilterDisplay: undefined });
            }
          });

        store._router.events
          .pipe(
            filter(event => event instanceof NavigationEnd),
            filter(() => !!getRouteData(store._router)[routeId]),
            takeUntilDestroyed(),
          )
          .subscribe(() => store._updatePageFilterFromUrl(getQueryParams()));

        dateTimeService.timezone$
          .pipe(
            pairwise(),
            filter(() => !!getRouteData(store._router)[routeId]),
            takeUntilDestroyed(),
          )
          .subscribe(([previousTimezone]) => store._setFilterDisplay(getQueryParams(), previousTimezone));

        if (getRouteData(store._router)[routeId]) {
          store._updatePageFilterFromUrl(getQueryParams());
          if (isEmpty(getQueryParams())) {
            store._updateUrlParams(store.pageFilter() ?? ({} as T), getQueryParams());
          }
        }
      },
    }),
  );
}

function getRouteData(router: Router): Record<string, unknown> {
  let data: Record<string, unknown> = {};
  for (
    let snapshot: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
    snapshot;
    snapshot = snapshot.firstChild
  ) {
    data = { ...data, ...snapshot.data };
  }
  return data;
}

function setPageFilter<T>(pageFilter: T) {
  return { pageFilter };
}
